import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "dark" | "danger";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  // The lime accent is the loudest thing on a black page, so it is spent on
  // exactly one action per view.
  primary:
    "bg-accent text-accent-content shadow-accent hover:bg-accent-hover active:translate-y-px",
  secondary:
    "bg-surface text-content border border-hairline-strong shadow-raise hover:bg-surface-raised",
  ghost: "text-content-muted hover:bg-surface-sunken hover:text-content",
  dark: "bg-void-950 text-void-50 shadow-raise hover:bg-void-800",
  danger: "bg-critical-500 text-void-950 shadow-raise hover:bg-critical-600",
};

// Pill controls, matching the reference. Radius scales with height so a small
// button does not read as a different shape from a large one.
const sizes: Record<Size, string> = {
  sm: "h-8 px-3.5 text-xs gap-1.5 rounded-(--radius-control)",
  md: "h-10 px-5 text-sm gap-2 rounded-(--radius-control)",
  lg: "h-12 px-7 text-base gap-2.5 rounded-(--radius-control)",
};

/**
 * Shared by <Button> and <LinkButton>. A navigation control must render as an
 * anchor, not a <button> wrapping one — nesting them is invalid HTML and
 * breaks middle-click, "open in new tab" and screen-reader semantics.
 */
export function buttonClasses({
  variant = "secondary",
  size = "md",
  fullWidth,
  className,
}: {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  className?: string;
} = {}) {
  return cn(
    "inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap",
    "transition-[background-color,box-shadow,transform] duration-150 ease-(--ease-out-soft)",
    "disabled:pointer-events-none disabled:opacity-50",
    variants[variant],
    sizes[size],
    fullWidth && "w-full",
    className,
  );
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    icon,
    fullWidth,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // Width is reserved while loading so the button does not resize and
      // shift the layout under the user's cursor mid-click.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap",
        "transition-[background-color,box-shadow,transform] duration-150 ease-(--ease-out-soft)",
        "disabled:pointer-events-none disabled:opacity-50",
        variants[variant],
        sizes[size],
        fullWidth && "w-full",
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 aria-hidden className="size-4 animate-spin" />
      ) : (
        icon
      )}
      {children}
    </button>
  );
});

/** Same visual treatment as <Button>, but navigates. */
export function LinkButton({
  to,
  variant,
  size,
  fullWidth,
  icon,
  className,
  children,
  ...props
}: Omit<LinkProps, "className"> & {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={buttonClasses({ variant, size, fullWidth, className })} {...props}>
      {icon}
      {children}
    </Link>
  );
}
